/**
 * Month-grid and chart helpers for the Occupancy page (POS2-1259 CP3).
 *
 * Every date here is a **civil date string** (`YYYY-MM-DD`), never a `Date` in the viewer's
 * timezone. A covers day is a civil day on both sides of the Waste-per-cover division
 * (decision 10), so building the grid from local `Date` objects would shift the whole month for
 * anyone east or west of the site.
 */
import type { DayCovers, EventType, OccupancyEvent } from "@/hooks/use-occupancy";
import type { Locale } from "@/i18n/config";
import { formatDate } from "@/lib/format";

/**
 * The mock's palette, lifted from `design/foodsight-covers-events.html`, plus the two hues the
 * POS2-1350 types needed.
 *
 * Pink and amber were **validated, not chosen by eye**: with blue, green, orange and purple
 * taken, they are the only pair that adds no new separation failure. Amber against the existing
 * orange is ΔE 15.3 for normal vision and 13.2 under deuteranopia — the two candidates that
 * looked better (a teal and a lighter blue) collide with green and blue respectively.
 *
 * Amber sits just under 3:1 against white (2.98), which obliges the identity to be carried in
 * text as well as colour. It already is, four times over: the legend chips, the tooltip, the day
 * panel's running order and the list view all name every event.
 *
 * Known and pre-existing: purple against blue is ΔE 11.4, below the 15 floor. That pair is the
 * mock's and shipped before this change; the same text relief covers it.
 */
export const TYPE_COLOURS: Record<EventType, string> = {
  wedding: "#2563eb",
  conference: "#067647",
  function: "#b54708",
  banquet: "#6941c6",
  general: "#c11574",
  other: "#ca8504",
};

/** Base covers are deliberately the quietest thing on the chart — events carry the colour. */
export const BASE_COLOUR = "#98a2b3";

/** `YYYY-MM` for a civil date string. */
export function monthKey(day: string): string {
  return day.slice(0, 7);
}

/** First civil date of a `YYYY-MM` month. */
export function monthStart(month: string): string {
  return `${month}-01`;
}

/** Last civil date of a `YYYY-MM` month. */
export function monthEnd(month: string): string {
  const [year, mon] = month.split("-").map(Number);
  // Day 0 of the next month is the last day of this one, and `Date.UTC` keeps it off the
  // viewer's clock.
  const last = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, "0")}`;
}

/** Step a `YYYY-MM` month by whole months. */
export function shiftMonth(month: string, delta: number): string {
  const [year, mon] = month.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** ISO weekday, 1 = Monday .. 7 = Sunday — the same convention the pattern table stores. */
export function isoWeekday(day: string): number {
  const [year, mon, date] = day.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, mon - 1, date)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

export function isWeekend(day: string): boolean {
  return isoWeekday(day) >= 6;
}

/**
 * The Mon–Sun grid for a month, padded with the out-of-month days that complete the first and
 * last weeks. Returns civil date strings so the cells can be keyed straight off the feed.
 */
export function monthGrid(month: string): string[] {
  const first = monthStart(month);
  const lead = isoWeekday(first) - 1;
  const cells: string[] = [];
  const [year, mon] = month.split("-").map(Number);

  const start = new Date(Date.UTC(year, mon - 1, 1 - lead));
  const lastOfMonth = Number(monthEnd(month).slice(8));
  const total = Math.ceil((lead + lastOfMonth) / 7) * 7;

  for (let index = 0; index < total; index += 1) {
    const cell = new Date(start);
    cell.setUTCDate(start.getUTCDate() + index);
    cells.push(cell.toISOString().slice(0, 10));
  }
  return cells;
}

/** A day filtered to the enabled event types, with its total recomputed to match. */
export function applyTypeFilter(day: DayCovers, enabled: Set<EventType | "base">): DayCovers {
  const events = day.events.filter((event) => enabled.has(event.event_type));
  const base = enabled.has("base") ? day.base : null;
  const eventCovers = events.reduce((sum, event) => sum + event.covers, 0);
  return {
    ...day,
    base,
    events,
    // Same rule as the server's: null only when there is nothing left to count, so toggling
    // every chip off reads as "nothing shown" rather than as a site with zero covers.
    total: base === null && events.length === 0 ? null : (base ?? 0) + eventCovers,
  };
}

export type MonthTotals = {
  total: number;
  dailyAverage: number | null;
  busiestDay: { date: string; total: number } | null;
};

/**
 * The card header's three figures. Days with no data are excluded from the average rather than
 * counted as zero — averaging over them would quietly understate a site that has entered half
 * the month (decision 8's coverage thinking, applied to the header).
 */
export function monthTotals(days: DayCovers[]): MonthTotals {
  const withData = days.filter((day) => day.total !== null);
  const total = withData.reduce((sum, day) => sum + (day.total ?? 0), 0);
  const busiest = withData.reduce<{ date: string; total: number } | null>((best, day) => {
    const dayTotal = day.total ?? 0;
    return best === null || dayTotal > best.total ? { date: day.date, total: dayTotal } : best;
  }, null);

  return {
    total,
    dailyAverage: withData.length > 0 ? Math.round(total / withData.length) : null,
    busiestDay: busiest,
  };
}

/** Events for one day, ordered the way the week view and day panel both present them. */
export function orderedEvents(events: OccupancyEvent[]): OccupancyEvent[] {
  return [...events].sort((a, b) => {
    if (a.start_time === null && b.start_time !== null) return 1;
    if (a.start_time !== null && b.start_time === null) return -1;
    if (a.start_time !== b.start_time)
      return (a.start_time ?? "").localeCompare(b.start_time ?? "");
    return a.name.localeCompare(b.name);
  });
}

/** `HH:MM` from a `HH:MM:SS` API time; null passes through so callers can omit the row. */
export function shortTime(time: string | null): string | null {
  return time === null ? null : time.slice(0, 5);
}

/**
 * Hold a time field to `HH:MM` as it is typed.
 *
 * Free text let a prefilled field accumulate on top of itself: clicking into "09:00" and typing
 * 9323 produced "00009323", which then blanked itself on blur and lost the entry outright. Four
 * digits is the whole of a time of day, so nothing past them is taken, and the colon is placed
 * rather than typed.
 *
 * Positional, `HHMM` — the first two digits are the hour whatever they are. That is what the
 * `hh:mm` placeholder promises, and it is why the parser below reads a lone third digit as the
 * minutes' tens.
 */
export function maskTimeOfDay(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/**
 * A 24-hour `HH:MM` from a masked field value, or null when it is not a time of day.
 *
 * The drawer parses rather than delegating to `<input type="time">`, whose 12- or 24-hour
 * rendering follows the **browser's** locale and not the app's. On a US-region machine the hour
 * segment caps at 12, so 13:00 cannot be typed at all — and Safari takes that from the macOS
 * region and ignores a `lang` attribute, so there is no markup that fixes it. Every other time in
 * the product is 24-hour, so the one field that asked for AM/PM was the field that was wrong.
 *
 * The field can only hold `H`, `HH`, `HH:M` or `HH:MM`, so a missing minutes digit is a trailing
 * zero: "18:3" is half past six, not three minutes past.
 */
export function parseTimeOfDay(raw: string): string | null {
  const masked = maskTimeOfDay(raw);
  if (masked === "") return null;
  const [rawHours, rawMinutes = ""] = masked.split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes.padEnd(2, "0") || "0");
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * The end a new event gets once a start is typed and none was given: two hours on, the usual
 * length of a service.
 *
 * Clamped at 23:59 rather than wrapped, because an event is single-day (decision 4) — an end
 * sorting before its own start would read as corrupt data in the running order, the conflict
 * check and the list alike.
 */
export function defaultEndTime(start: string): string {
  const [hours, minutes] = start.split(":").map(Number);
  const total = hours * 60 + minutes + 2 * 60;
  if (total >= 24 * 60) return "23:59";
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * The drawer's live expected-waste readout (decision 13).
 *
 * Deliberately not rounded to a whole kilo: at 45 g per cover a 40-cover function is 1.8 kg, and
 * rounding that to 2 would move the figure by more than 10% on exactly the small bookings where
 * the manager is deciding whether the estimate is worth trusting. The money figure is dropped
 * rather than guessed when the site has no cost history.
 */
export function formatExpectedWaste(
  covers: number,
  basis: { grams_per_cover: number; cost_per_kg: number | null; currency: string },
): { headline: string; gramsPerCover: number } {
  const kg = (covers * basis.grams_per_cover) / 1000;
  const kgText = `${kg.toFixed(kg < 10 ? 1 : 0)} kg`;
  if (basis.cost_per_kg === null) {
    return { headline: kgText, gramsPerCover: basis.grams_per_cover };
  }
  const money = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: basis.currency,
    maximumFractionDigits: 0,
  }).format(kg * basis.cost_per_kg);
  return { headline: `${kgText} · ${money}`, gramsPerCover: basis.grams_per_cover };
}

/**
 * The Mon–Sun week containing `date`, taken from the month's feed.
 *
 * Days outside the visible month are not in the feed, so a week straddling a month boundary
 * comes back short rather than padded with invented empty days — the week view renders what the
 * range actually covers, and stepping the month brings the rest.
 */
export function weekOf(days: DayCovers[], date: string): DayCovers[] {
  const weekday = isoWeekday(date);
  const [year, mon, dayOfMonth] = date.split("-").map(Number);
  const monday = new Date(Date.UTC(year, mon - 1, dayOfMonth - (weekday - 1)));
  const wanted = new Set(
    Array.from({ length: 7 }, (_, offset) => {
      const cell = new Date(monday);
      cell.setUTCDate(monday.getUTCDate() + offset);
      return cell.toISOString().slice(0, 10);
    }),
  );
  return days.filter((day) => wanted.has(day.date));
}

export type RangeCovers = {
  /** Null when nothing countable remains — "no covers data", never zero (decision 8). */
  covers: number | null;
  daysCounted: number;
  daysNoData: number;
  /** Days dropped by decision 9 — part of a weekly-entered week the range cuts in half. */
  daysExcludedWeekly: number;
  /** True when every day in the range carries entered data. */
  fullyEntered: boolean;
  /** The last person to write any counted day, for the provenance chip. A range can hold days
   *  from several people through two surfaces, so this names the most recent — the same rule
   *  food spend uses, where the chip carries the date beside the name. */
  enteredById: string | null;
  enteredByName: string | null;
  enteredAt: string | null;
};

function isoWeekBounds(day: string): { start: string; end: string } {
  const weekday = isoWeekday(day);
  const [year, mon, dayOfMonth] = day.split("-").map(Number);
  const monday = new Date(Date.UTC(year, mon - 1, dayOfMonth - (weekday - 1)));
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
}

/**
 * Sum a covers feed over a range under decision 9's granularity rule.
 *
 * A **weekly-entered** week is one figure the customer gave for seven days; the daily split is
 * ours, not theirs. It counts only when the range contains the whole Mon–Sun block — a range
 * cutting one in half treats those days as no-covers-data rather than inventing a daily figure
 * nobody entered. The server applies the identical rule in `sum_covers_for_range`, and the two
 * must not drift: the KPI and the report are the same number seen from two places.
 */
/**
 * How a weekly-entered week behaves when the range does not hold all seven of its days.
 *
 * `"whole-weeks"` is decision 9: the week counts only as a complete Mon–Sun block, and a range
 * cutting one treats those days as no-covers-data. **The report uses this.** A figure in a PDF
 * that came from slicing a week by the site's pattern shape is a claim the customer never made,
 * and there is no period control beside it to explain the shortfall.
 *
 * `"partial-weeks"` counts whatever days the range holds. **The dashboard uses this**, for two
 * reasons. The reader is exploring, with the period on screen and the "Entered weekly" note
 * saying where the figure came from. And it is what shipped *before* POS2-1259 —
 * `estimateRangeCovers` prorated a week across a sub-range — so excluding here would be a
 * regression against what users have in production today, not a new strictness.
 */
export type WeeklyWeeks = "whole-weeks" | "partial-weeks";

export function sumCoversForRange(
  feed: DayCovers[],
  rangeStart: string,
  rangeEnd: string,
  weeks: WeeklyWeeks,
): RangeCovers {
  let total = 0;
  let counted = 0;
  let noData = 0;
  let excludedWeekly = 0;
  let latest: DayCovers | null = null;

  for (const day of feed) {
    if (day.total === null) {
      noData += 1;
      continue;
    }
    if (day.weekly_entered && weeks === "whole-weeks") {
      const week = isoWeekBounds(day.date);
      if (week.start < rangeStart || week.end > rangeEnd) {
        excludedWeekly += 1;
        continue;
      }
    }
    total += day.total;
    counted += 1;
    if (day.entered_at && (latest?.entered_at == null || day.entered_at > latest.entered_at)) {
      latest = day;
    }
  }

  return {
    covers: counted > 0 ? total : null,
    daysCounted: counted,
    daysNoData: noData,
    daysExcludedWeekly: excludedWeekly,
    fullyEntered: counted > 0 && noData === 0 && excludedWeekly === 0,
    enteredById: latest?.entered_by_id ?? null,
    enteredByName: latest?.entered_by_name ?? null,
    enteredAt: latest?.entered_at ?? null,
  };
}

/**
 * Split a weekly figure across Mon–Sun **by the site's weekday pattern shape**, preserving the
 * total exactly (POS2-1259 CP5).
 *
 * Largest remainder, like the backfill: `Math.round(total * share)` per day loses or invents
 * covers on almost every week, and these are the figures Waste per cover divides by. With no
 * pattern, or a pattern summing to zero, the split is even — a site that has never given a shape
 * gets no shape invented for it.
 */
export function splitWeekByPattern(
  total: number,
  weekStart: string,
  pattern: { weekday: number; covers: number }[] | null,
): number[] {
  const weekdays = Array.from(
    { length: 7 },
    (_, offset) => ((isoWeekday(weekStart) - 1 + offset) % 7) + 1,
  );
  const shape = weekdays.map(
    (weekday) => pattern?.find((day) => day.weekday === weekday)?.covers ?? 0,
  );
  const shapeTotal = shape.reduce((sum, value) => sum + value, 0);
  const weights = shapeTotal > 0 ? shape : Array(7).fill(1);
  const weightTotal = shapeTotal > 0 ? shapeTotal : 7;

  const exact = weights.map((weight) => (total * weight) / weightTotal);
  const floors = exact.map(Math.floor);
  let remainder = total - floors.reduce((sum, value) => sum + value, 0);

  // Hand the remainder to the days with the largest fractional part, so the split leans towards
  // the days the site's own pattern says are busiest.
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);

  const result = [...floors];
  for (const { index } of order) {
    if (remainder <= 0) break;
    result[index] += 1;
    remainder -= 1;
  }
  return result;
}

/** The seven civil dates of the Mon–Sun week beginning `weekStart`. */
export function weekDates(weekStart: string): string[] {
  const [year, mon, day] = weekStart.split("-").map(Number);
  const start = new Date(Date.UTC(year, mon - 1, day));
  return Array.from({ length: 7 }, (_, offset) => {
    const cell = new Date(start);
    cell.setUTCDate(start.getUTCDate() + offset);
    return cell.toISOString().slice(0, 10);
  });
}

export type EnteredDay = { date: string; covers: number; source: string };

export type EnteredWeek = {
  /** Monday of the week, the key the widget's picker snaps to. */
  weekStart: string;
  weekEnd: string;
  /** Sum of the entered days in this week — what the widget showed when it was saved. */
  covers: number;
  /** How many of the seven days carry a figure. Fewer than 7 means the week is part-entered. */
  daysEntered: number;
  /** True when any day in the week was edited by hand after the week was split. */
  hasDailyEdit: boolean;
  /** The week's own days, oldest first — what the row expands to show. Carried rather than
   *  recomputed because undoing a removal has to restore each day's figure *and* its source:
   *  restoring a widget split as `page` would quietly take the week out of decision 9's
   *  weekly-entered block. */
  days: EnteredDay[];
};

/**
 * Group entered days into the Mon–Sun weeks the homepage widget deals in (POS2-1259 CP5).
 *
 * The widget's contract is weeks — "one figure for the week" — but the store holds days, because
 * that is what makes events add on top and what the KPIs divide by. So the list has to put them
 * back together: the spec asks for a **weeks** list, not a days list, and a user who entered one
 * figure wants to see that one figure again rather than the seven rows it became.
 *
 * `hasDailyEdit` is the flag worth surfacing: a week somebody has since corrected on the
 * Occupancy page is no longer the figure they typed here, and re-saving it would overwrite that
 * correction.
 */
export function groupOverridesIntoWeeks(
  overrides: { date: string; covers: number; source: string }[],
): EnteredWeek[] {
  const byWeek = new Map<string, EnteredWeek>();

  for (const override of overrides) {
    const weekday = isoWeekday(override.date);
    const [year, mon, day] = override.date.split("-").map(Number);
    const monday = new Date(Date.UTC(year, mon - 1, day - (weekday - 1)));
    const weekStart = monday.toISOString().slice(0, 10);

    const existing = byWeek.get(weekStart);
    if (existing) {
      existing.covers += override.covers;
      existing.daysEntered += 1;
      existing.hasDailyEdit = existing.hasDailyEdit || override.source === "page";
      existing.days.push(override);
      continue;
    }
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    byWeek.set(weekStart, {
      weekStart,
      weekEnd: sunday.toISOString().slice(0, 10),
      covers: override.covers,
      daysEntered: 1,
      hasDailyEdit: override.source === "page",
      days: [override],
    });
  }

  // Newest first, like the food spend weeks list. Days within a week run the other way — a week
  // reads Monday to Sunday however the rows arrived.
  for (const week of byWeek.values()) {
    week.days.sort((a, b) => a.date.localeCompare(b.date));
  }
  return [...byWeek.values()].sort((a, b) => b.weekStart.localeCompare(a.weekStart));
}

/**
 * The day label the expanded entered-weeks list reads with (POS2-1259 CP5). The range above it
 * comes from `formatDateRange`, which every covered-period line in the app shares.
 *
 * **Formatted in UTC on purpose.** These are civil date strings, and `new Date("2026-09-14")` is
 * UTC midnight — rendered in the viewer's zone, anyone west of Greenwich would see the 13th. The
 * same reason the grid is built with `Date.UTC`.
 */

/** "Mon 14" — the weekday and the day, which is what identifies a day inside its own week. The
 *  month is already on the row above it. */
export function formatDayLabel(locale: Locale, date: string): string {
  return formatDate(locale, date, { weekday: "short", day: "numeric", timeZone: "UTC" });
}
